import { instantToWallTime } from './instant-to-wall-time';
import { toLogicalDateString } from './to-logical-date-string';

/**
 * Today's date as a `YYYY-MM-DD` logical date.
 *
 * **This is the only correct way to default a date field to "today."** The
 * shape it replaces — `new Date().toISOString().split('T')[0]` — takes the
 * **UTC** date part of the current instant, which for every US zone has already
 * rolled over to tomorrow by late afternoon (5 PM Pacific / 8 PM Eastern). That
 * silently post-dates financial records by a day for the whole evening shift.
 *
 * **"Today" is the auction house's day** (ADR 024 — Time Model § 3c; Michael
 * Lawrie, 2026-09-25, superseding BL-1625 AC 18): every admin surface and every
 * server path passes the house zone (`tenants.time_zone` — `useTimezone()` on
 * the client, a tenant read on the server). `zone` is REQUIRED so the old
 * zone-less form — the browser's day on screen, the UTC day on Cloud Run —
 * cannot be called at all (BUG-1832).
 *
 * @param zone - IANA zone to resolve "today" in — the house's zone. Falls back
 *   to the runtime's zone if `zone` is invalid, rather than throwing on a
 *   display path; that fallback is a guard, not a rule.
 * @param now - The instant whose day it is; defaults to the clock. A service
 *   with an injected clock passes its own (BL-2374's payment-date window).
 * @returns Today in `zone`, `YYYY-MM-DD`.
 * @sideEffect none beyond reading the clock.
 */
export function todayLogicalDate(zone: string, now: Date = new Date()): string {

    const wall = instantToWallTime({ instant: now, zone });
    if (wall) return toLogicalDateString({ year: wall.year, month: wall.month, day: wall.day });

    // Invalid zone: local getters on a local instant, read through the same lens.
    return toLogicalDateString({
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        day: now.getDate(),
    });
}
